import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Edit2, Trash2, Plus, Search, X } from 'lucide-react';
import ExportButtons from './ExportButtons';

const CitiesManager = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentCity, setCurrentCity] = useState(null);
  const [formData, setFormData] = useState({
    country: '',
    province: '',
    city: '',
    timezone: 'America/Toronto',
    population: 0
  });

  // Available timezones for Canada
  const canadaTimezones = [
    { value: 'America/St_Johns', label: 'Newfoundland Time (NST/NDT)' },
    { value: 'America/Halifax', label: 'Atlantic Time (AST/ADT)' },
    { value: 'America/Moncton', label: 'Atlantic Time - Moncton (AST/ADT)' },
    { value: 'America/Toronto', label: 'Eastern Time (EST/EDT)' },
    { value: 'America/Montreal', label: 'Eastern Time - Montreal (EST/EDT)' },
    { value: 'America/Thunder_Bay', label: 'Eastern Time - Thunder Bay (EST/EDT)' },
    { value: 'America/Winnipeg', label: 'Central Time (CST/CDT)' },
    { value: 'America/Regina', label: 'Central Standard Time (CST) - No DST' },
    { value: 'America/Rainy_River', label: 'Central Time - Rainy River (CST/CDT)' },
    { value: 'America/Edmonton', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Yellowknife', label: 'Mountain Time - Yellowknife (MST/MDT)' },
    { value: 'America/Dawson_Creek', label: 'Mountain Standard Time (MST) - No DST' },
    { value: 'America/Fort_Nelson', label: 'Mountain Standard Time - Fort Nelson (MST)' },
    { value: 'America/Creston', label: 'Mountain Standard Time - Creston (MST)' },
    { value: 'America/Vancouver', label: 'Pacific Time (PST/PDT)' },
    { value: 'America/Whitehorse', label: 'Mountain Standard Time - Yukon (MST)' },
    { value: 'America/Iqaluit', label: 'Eastern Time - Iqaluit (EST/EDT)' },
    { value: 'America/Rankin_Inlet', label: 'Central Time - Rankin Inlet (CST/CDT)' },
    { value: 'America/Cambridge_Bay', label: 'Mountain Time - Cambridge Bay (MST/MDT)' },
    { value: 'America/Goose_Bay', label: 'Atlantic Time - Goose Bay (AST/ADT)' },
  ];

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    // Filter cities based on search term
    if (searchTerm.trim() === '') {
      setFilteredCities(cities);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = cities.filter(city =>
        city.country.toLowerCase().includes(term) ||
        city.province.toLowerCase().includes(term) ||
        city.city.toLowerCase().includes(term)
      );
      setFilteredCities(filtered);
    }
  }, [searchTerm, cities]);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/cities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCities(data.cities || []);
        setFilteredCities(data.cities || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      alert('Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({ country: 'Canada', province: '', city: '' });
    setIsAddModalOpen(true);
  };

  const handleEdit = (city) => {
    setCurrentCity(city);
    setFormData({
      country: city.country,
      province: city.province,
      city: city.city,
      timezone: city.timezone || 'America/Toronto',
      population: city.population || 0
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (cityId) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/cities/${cityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('City deleted successfully');
        fetchCities();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete city');
      }
    } catch (error) {
      console.error('Error deleting city:', error);
      alert('Failed to delete city');
    }
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    
    if (!formData.country || !formData.province || !formData.city || !formData.timezone) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/cities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('City added successfully');
        setIsAddModalOpen(false);
        fetchCities();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add city');
      }
    } catch (error) {
      console.error('Error adding city:', error);
      alert('Failed to add city');
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!formData.country || !formData.province || !formData.city || !formData.timezone) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/cities/${currentCity.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('City updated successfully');
        setIsEditModalOpen(false);
        fetchCities();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update city');
      }
    } catch (error) {
      console.error('Error updating city:', error);
      alert('Failed to update city');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">City Settings</h2>
        <p className="text-gray-600 dark:text-gray-400">Manage cities and provinces for trainer locations</p>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-6 gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search by country, province, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={handleAdd} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Add City
        </Button>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={filteredCities}
        columns={[
          { header: 'City ID', key: 'id', accessor: (row) => row.id || 'N/A' },
          { header: 'Country', key: 'country', accessor: (row) => row.country || 'N/A' },
          { header: 'Province', key: 'province', accessor: (row) => row.province || 'N/A' },
          { header: 'City', key: 'city', accessor: (row) => row.city || 'N/A' }
        ]}
        filename="cities-settings"
      />

      {/* Cities Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Province
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timezone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Population
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading cities...
                  </td>
                </tr>
              ) : filteredCities.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'No cities found matching your search' : 'No cities available'}
                  </td>
                </tr>
              ) : (
                filteredCities.map((city, index) => (
                  <tr key={city.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {city.country}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {city.province}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {city.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {canadaTimezones.find(tz => tz.value === city.timezone)?.label || city.timezone || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {city.population ? city.population.toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(city)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(city.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Results Count */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredCities.length} of {cities.length} cities
      </div>

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold dark:text-white">Add New City</h3>
              <button onClick={() => setIsAddModalOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitAdd} className="space-y-4">
              <div>
                <Label>Country</Label>
                <Input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., Canada"
                  required
                />
              </div>
              <div>
                <Label>Province/State</Label>
                <Input
                  type="text"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  placeholder="e.g., Ontario"
                  required
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Toronto"
                  required
                />
              </div>
              <div>
                <Label>Timezone</Label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  {canadaTimezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Population (optional)</Label>
                <Input
                  type="number"
                  value={formData.population}
                  onChange={(e) => setFormData({ ...formData, population: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 100000"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Add City
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold dark:text-white">Edit City</h3>
              <button onClick={() => setIsEditModalOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitEdit} className="space-y-4">
              <div>
                <Label>Country</Label>
                <Input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Province/State</Label>
                <Input
                  type="text"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Timezone</Label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  {canadaTimezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Population</Label>
                <Input
                  type="number"
                  value={formData.population}
                  onChange={(e) => setFormData({ ...formData, population: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Update City
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CitiesManager;
